import { Menu, Transition } from '@headlessui/react'
import {
  EllipsisVerticalIcon,
  PencilIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Tooltip as MantineTooltip } from '@mantine/core'
import { usePostHog } from 'posthog-js/react'
import React, { Fragment } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { useRunRiskListReviewMutation } from '../../../redux/api/project-risk-api-slice'
import { setModal } from '../../../redux/application-slice'
import { RiskListProps } from '../../../shared/interfaces/project/risk/risk-inteface'
import { POSTHOG } from '../../../utils/posthog-constants'
import { MODAL_TYPES } from '../../modals/modal-controller'

const RiskList: React.FC<RiskListProps> = ({
  id,
  name,
  projectUuid,
  userPermissions,
  isLoadingRiskLists,
}) => {
  const dispatch = useDispatch()
  const [runRiskListReview] = useRunRiskListReviewMutation()
  const location = useLocation()
  const navigate = useNavigate()
  const posthog = usePostHog()

  const canShare = userPermissions?.can_share

  // Check if this is a default risk list
  const isDefaultRiskList = [
    'Contract Review',
    'Project Review',
    'Operational',
  ].includes(name)

  const handleEditClick = () => {
    dispatch(
      setModal({ modal: MODAL_TYPES.EDIT_RISK_LIST, props: { id, name } })
    )
  }

  const handleDeleteClick = () => {
    dispatch(
      setModal({ modal: MODAL_TYPES.DELETE_RISK_LIST, props: { id, name } })
    )
  }

  const handleViewRisksClick = () => {
    navigate(`${location.pathname}/${id}`)
  }

  const handleRunClick = async () => {
    try {
      await runRiskListReview({
        risklist_template_id: id,
        project_uuid: projectUuid,
      }).unwrap()
      posthog?.capture(POSTHOG.custom_risklisttemplate_riskreview_run_called, {
        risk_list_template_id: id,
        project_uuid: projectUuid,
      })
      toast.success(`Risk list review for ${name} started successfully`)
    } catch {
      toast.error(`Failed to start risk list review for ${name}`)
    }
  }

  const handleShareClick = () => {
    dispatch(
      setModal({
        modal: MODAL_TYPES.SHARE_RISK_LIST_TEMPLATE,
        props: { id, name, canShare },
      })
    )
  }

  const enabledButtonClassNames =
    'rounded-md border-2 border-[#ffffff00] px-3 py-1 text-sm font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:!text-blue-600 group-hover:text-black'

  const disabledButtonClassNames =
    'cursor-not-allowed bg-gray-100 text-gray-400'

  return (
    <div className="mb-2 flex items-center justify-between rounded-lg border-2 border-gray-50 p-4 hover:border-blue-500">
      <span className="text-lg font-medium">{name}</span>
      <div className="flex space-x-2">
        <MantineTooltip
          label="Click to re-analyze all risks in this list"
          position="bottom"
        >
          <button
            onClick={handleRunClick}
            className={`rounded px-3 py-1 text-sm font-medium ${
              userPermissions?.can_run
                ? enabledButtonClassNames
                : disabledButtonClassNames
            }`}
            aria-label="Run"
            disabled={!userPermissions?.can_run}
            data-tooltip-id="button-tooltip"
            data-tooltip-content={
              !userPermissions?.can_run
                ? 'You do not have permission to perform this action.'
                : undefined
            }
          >
            Run
          </button>
        </MantineTooltip>
        <MantineTooltip
          label="View all risks and questions in this list"
          position="bottom"
        >
          <button
            onClick={handleViewRisksClick}
            className={`rounded px-3 py-1 text-sm font-medium ${
              userPermissions?.can_view && !isDefaultRiskList
                ? enabledButtonClassNames
                : disabledButtonClassNames
            }`}
            aria-label="View Details"
            disabled={!userPermissions?.can_view || isDefaultRiskList}
            data-tooltip-id="button-tooltip"
            data-tooltip-content={
              !userPermissions?.can_view
                ? 'You do not have permission to perform this action.'
                : isDefaultRiskList
                  ? 'View details is not available for default risk lists.'
                  : undefined
            }
          >
            View Details
          </button>
        </MantineTooltip>

        <Menu as="div" className="relative inline-block text-left">
          <MantineTooltip
            label={
              isDefaultRiskList
                ? 'Actions not available for default risk lists'
                : 'More actions'
            }
            position="bottom"
          >
            <Menu.Button
              className={`flex items-center rounded-md px-3 py-1 text-sm font-medium ${
                !isDefaultRiskList
                  ? 'text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:!text-blue-600'
                  : 'cursor-not-allowed bg-gray-100 text-gray-400'
              }`}
              data-testid="risk-list-dropdown-button"
              disabled={isDefaultRiskList}
            >
              <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
          </MantineTooltip>

          {!isDefaultRiskList && (
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item
                    disabled={
                      !userPermissions?.can_change || isLoadingRiskLists
                    }
                  >
                    {({ active }) => (
                      <button
                        onClick={handleEditClick}
                        disabled={
                          !userPermissions?.can_change || isLoadingRiskLists
                        }
                        className={`${
                          active &&
                          userPermissions?.can_change &&
                          !isLoadingRiskLists
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700'
                        } ${
                          !userPermissions?.can_change || isLoadingRiskLists
                            ? 'cursor-not-allowed opacity-50'
                            : ''
                        } flex w-full items-center px-4 py-2 text-sm`}
                        data-tooltip-id="dropdown-tooltip"
                        data-tooltip-content={
                          !userPermissions?.can_change
                            ? 'You do not have permission to perform this action.'
                            : isLoadingRiskLists
                              ? 'Please wait while risk lists are loading.'
                              : undefined
                        }
                      >
                        <PencilIcon className="mr-3 h-5 w-5" />
                        Edit title
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item disabled={!canShare}>
                    {({ active }) => (
                      <button
                        onClick={handleShareClick}
                        disabled={!canShare}
                        className={`${
                          active && canShare
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700'
                        } ${
                          !canShare ? 'cursor-not-allowed opacity-50' : ''
                        } flex w-full items-center px-4 py-2 text-sm`}
                        data-tooltip-id="dropdown-tooltip"
                        data-tooltip-content={
                          !canShare
                            ? 'You do not have permission to share this risk list.'
                            : undefined
                        }
                      >
                        <ShareIcon className="mr-3 h-5 w-5" />
                        Share
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item
                    disabled={
                      !userPermissions?.can_change || isLoadingRiskLists
                    }
                  >
                    {({ active }) => (
                      <button
                        onClick={handleDeleteClick}
                        disabled={
                          !userPermissions?.can_change || isLoadingRiskLists
                        }
                        className={`${
                          active &&
                          userPermissions?.can_change &&
                          !isLoadingRiskLists
                            ? 'bg-gray-100 text-gray-900'
                            : ''
                        } ${
                          !userPermissions?.can_change || isLoadingRiskLists
                            ? 'cursor-not-allowed opacity-50'
                            : ''
                        } flex w-full items-center px-4 py-2 text-sm ${
                          userPermissions?.can_change && !isLoadingRiskLists
                            ? 'text-red-600'
                            : 'text-red-300'
                        }`}
                        data-tooltip-id="dropdown-tooltip"
                        data-tooltip-content={
                          !userPermissions?.can_change
                            ? 'You do not have permission to perform this action.'
                            : isLoadingRiskLists
                              ? 'Please wait while risk lists are loading.'
                              : undefined
                        }
                      >
                        <TrashIcon className="mr-3 h-5 w-5" />
                        Delete
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          )}
        </Menu>

        <Tooltip id="button-tooltip" place="left" />
        <Tooltip id="dropdown-tooltip" place="left" />
      </div>
    </div>
  )
}

export default RiskList
